import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { GameMode, Complexity, HandicapLevel, AIDifficulty } from "../../../shared/types";
import { formatTimeLimit } from "../../../shared/utils";

interface Props {
  onStartGame: (settings: {
    mode: GameMode; complexity: Complexity;
    timeLimitMs: number; aiDifficulty: AIDifficulty | null; handicap: HandicapLevel;
  }) => void;
}

export default function RoomLobby({ onStartGame }: Props) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<GameMode>("grid");
  const [complexity, setComplexity] = useState<Complexity>("low");
  const [timeLimit, setTimeLimit] = useState(180000);
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty | null | "random">(null);

  const timeOptions = [180000, 210000, 240000, 270000, 300000];
  const aiOptions: { value: AIDifficulty | null | "random"; label: string; icon?: string }[] = [
    { value: null, label: "PvP (Human Opponent)" },
    { value: "easy", label: "Solo vs Easy AI" },
    { value: "medium", label: "Solo vs Medium AI" },
    { value: "hard", label: "Solo vs Hard AI" },
    { value: "random", label: "Solo vs Random AI", icon: "🎲" },
  ];

  return (
    <div className="bg-board-cell rounded-lg p-4 sm:p-6 w-full max-w-md mx-auto space-y-4 sm:space-y-6">
      <h2 className="text-xl font-bold text-vsw-400">Room Settings</h2>

      <div>
        <label className="text-vsw-200 text-sm block mb-2">Game Mode</label>
        <div className="flex gap-2">
          {(["arcade", "grid"] as GameMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 py-2 rounded font-medium ${
                mode === m ? "bg-vsw-500 text-white" : "bg-board-bg text-vsw-300"
              }`}
            >
              {m === "arcade" ? "Arcade" : "Grid"}
            </button>
          ))}
        </div>
        <p className="text-vsw-300 text-xs mt-1">
          {mode === "arcade"
            ? "Open board. Place words anywhere to build the highest score."
            : "Puzzle grid with clues. Fill white squares to complete the word grid."}
        </p>
      </div>

      <div>
        <label className="text-vsw-200 text-sm block mb-2">Opponent</label>
        <select
          value={aiDifficulty ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            setAiDifficulty(val === "" ? null : val as AIDifficulty | "random");
          }}
          className="w-full bg-board-bg text-vsw-200 p-2 rounded border border-board-border"
        >
          {aiOptions.map((opt) => (
            <option key={opt.label} value={opt.value ?? ""}>
              {opt.icon ? `${opt.icon} ` : ""}{opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-vsw-200 text-sm block mb-2">Complexity</label>
        <div className="flex gap-2">
          {(["low", "medium", "high"] as Complexity[]).map((c) => (
            <button
              key={c}
              onClick={() => setComplexity(c)}
              className={`flex-1 py-2 rounded font-medium ${
                complexity === c ? "bg-vsw-500 text-white" : "bg-board-bg text-vsw-300"
              }`}
            >
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-vsw-200 text-sm block mb-2">Time Limit</label>
        <select
          value={timeLimit}
          onChange={(e) => setTimeLimit(Number(e.target.value))}
          className="w-full bg-board-bg text-vsw-200 p-2 rounded border border-board-border"
        >
          {timeOptions.map((t) => (
            <option key={t} value={t}>{formatTimeLimit(t)}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => {
            let resolvedAI = aiDifficulty;
            if (resolvedAI === "random") {
              const diffs: AIDifficulty[] = ["easy", "medium", "hard"];
              resolvedAI = diffs[Math.floor(Math.random() * diffs.length)];
            }
            onStartGame({ mode, complexity, timeLimitMs: timeLimit, aiDifficulty: resolvedAI, handicap: "none" });
          }}
          className="flex-1 bg-vsw-500 text-white py-3 rounded-lg font-bold hover:bg-vsw-400"
        >
          {aiDifficulty ? "Start Solo Game" : "Create Room"}
        </button>
        <button
          onClick={() => navigate("/leaderboard")}
          className="flex-1 bg-board-border text-vsw-200 py-3 rounded-lg font-bold"
        >
          Leaderboard
        </button>
      </div>
    </div>
  );
}
